import type { FetchOptions } from 'ofetch'

import { MAX_RETRIES } from '@/constants/maxRetires'

interface ExtendedFetchOptions extends FetchOptions {
  _endpoint?: string
  _refreshed?: boolean | null
  suppressGlobalErrorModal?: boolean
  abortOnNavigation?: boolean
}

const { addController, removeController } = useAbortController()

let tokenRefreshPromise: Promise<boolean> | null = null

// Helper function for network error detection
export const isNetworkError = (error: any) => {
  return (
    error?.message?.includes('Failed to fetch') ||
    error?.message?.includes('NetworkError') ||
    error?.message?.includes('Load failed') ||
    error?.name === 'NetworkError' ||
    error?.code === 'NETWORK_ERROR' ||
    error?.constructor?.name === 'FetchError' ||
    (!error?.response && !error?.status && !error?.statusCode && !error?.data)
  )
}

// Exponential backoff retry function
export const retryWithBackoff = async (fn: () => Promise<any>, maxRetries = MAX_RETRIES) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn()
    } catch (error) {
      // Only retry for network errors, not validation/auth errors
      if (i === maxRetries - 1 || !isNetworkError(error)) {
        throw error
      }

      // Wait with exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, i) * 1000
      console.log(`Request attempt ${i + 1} failed, retrying in ${delay}ms...`)
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }
}

const apiInstance = () => {
  const {
    public: { API_BASE, API_VERSION }
  } = useRuntimeConfig()

  const baseURL = `${API_BASE}/${API_VERSION}`

  const instance = ref<typeof $fetch | null>(null)

  const handleTokenRefresh = async (options: ExtendedFetchOptions) => {
    const { clearCookies, fetchRefreshToken, loginRedirect } = useAuth()

    if (!tokenRefreshPromise) {
      tokenRefreshPromise = fetchRefreshToken().finally(() => {
        tokenRefreshPromise = null
      })
    }

    const success = await tokenRefreshPromise

    const { logSessionEvent } = useSessionAudit()

    if (!success) {
      logSessionEvent('session:token_refresh:failure')
      clearCookies()
      await loginRedirect({ redirectQuery: true })
      // Throw to stop the interceptor chain — do not fall through
      throw new Error('Token refresh failed')
    }

    logSessionEvent('session:token_refresh:success')

    options._refreshed = true

    if (options.signal) delete options.signal

    return instance.value!
  }

  instance.value = $fetch.create({
    baseURL,

    onRequest: async ({ options }: { request: Request; options: ExtendedFetchOptions }) => {
      options.headers = new Headers(options.headers)

      const { bearerToken } = useAuthStore()

      if (bearerToken) {
        options.headers.set('Authorization', `Bearer ${bearerToken}`)
      }
    },

    onResponse: async ({ response, options }: { response: Response; options: ExtendedFetchOptions }) => {
      options._refreshed = null

      return response
    },

    onResponseError: async ({ response, options }: { response: Response; options: ExtendedFetchOptions }) => {
      // Handle 401 Unauthorized - refresh the token and retry the original request
      if (response.status === 401 && !options._refreshed) {
        const { isAuthenticated } = useUserStore()

        if (isAuthenticated) {
          try {
            const retryInstance = await handleTokenRefresh(options)

            // Retry the original request with the refreshed token
            const { bearerToken } = useAuthStore()
            const retryHeaders = new Headers(options.headers)
            if (bearerToken) {
              retryHeaders.set('Authorization', `Bearer ${bearerToken}`)
            }

            return retryInstance(options._endpoint!, {
              ...options,
              headers: retryHeaders
            })
          } catch {
            // handleTokenRefresh threw (refresh failed) — reject is fine
            return Promise.reject(response)
          }
        }
      }

      // Always reject to let calling code handle the error
      return Promise.reject(response)
    },

    onRequestError({ error, options }: { options: ExtendedFetchOptions; error: Error }) {
      // Handling for non-TypeErrors or NetworkErrors with messaging to suggest a
      // connection failure / timeout before reaching the server (ex. status code: 0)

      // commenting this out for now as isNetworkError was introduced along side retryWithBackoff
      // which we are no longer using and isNetworkError blocks the refresh mechanism from running
      // when we get back a 401 reponse
      // if (!(error instanceof TypeError) || isNetworkError(error)) {
      if (!(error instanceof TypeError)) {
        return Promise.reject(error)
      }

      const { isAuthenticated } = useUserStore()

      if (options._refreshed || !isAuthenticated) {
        return Promise.reject(error)
      }

      return handleTokenRefresh(options)
    }
  })

  return instance
}

export const useApi = (
  endpoint: string,
  initialOptions: ExtendedFetchOptions = {},
  context: 'page' | 'dialog' = 'page',
  inCoverageFlow = false
) => {
  let controller: AbortController | undefined

  const execute = async (override: Partial<ExtendedFetchOptions> = {}) => {
    controller?.abort()

    controller = new AbortController()

    const opts: ExtendedFetchOptions = {
      ...initialOptions,
      ...override,
      _endpoint: endpoint,
      signal: controller.signal
    }

    if (opts.abortOnNavigation !== false) {
      addController(controller)
    }

    const instance = apiInstance().value!

    const { startLoading, stopLoading } = useApiStore()

    startLoading(context)

    try {
      return await instance(endpoint, opts).finally(() => {
        removeController(controller!)

        setTimeout(() => {
          stopLoading(context)
        })
      })
    } catch (error) {
      const { shouldShowModal, showErrorModal } = useGlobalErrorModal()

      const apiError = error?._data || error?.response?._data || error?.data || error?.response || error

      if (shouldShowModal(apiError, { suppress: opts.suppressGlobalErrorModal })) {
        if (apiError?.title === 'RENEWAL_TOO_CLOSE') {
          const { showErrorModal: showRenewalTooCloseModal } = useRenewalTooCloseErrorModal()
          showRenewalTooCloseModal()
          throw error
        }

        await showErrorModal(inCoverageFlow)
        throw error
      }

      throw error
    }
  }

  const response = execute()

  return {
    response,
    retry: execute,
    cancel: () => controller?.abort()
  }
}
